import {
  pullResultSchema,
  pushResultSchema,
  type OutboxOp,
  type PullResult,
  type PushResult,
} from '@cadencia/contracts';
import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';
import { keysToSnake } from './case.js';
import type { SyncTransport } from './types.js';

/*
 * Lo que llega por el cable se valida con `entity` ABIERTA, no con el enum.
 *
 * Con el enum cerrado, una fila de una tabla que el servidor ya tiene y este
 * cliente todavia no —un dispositivo una version por detras— hacia lanzar el
 * `parse` aqui, antes de que el motor pudiera saltarsela como ya sabe hacer
 * (`SYNC_UNKNOWN_ENTITY`). La bajada se atascaba en esa pagina para siempre, y
 * la subida, que tambien devuelve filas, gastaba un intento de TODO el lote en
 * cada ciclo aunque el servidor lo hubiera aplicado. Paso el 22/09/2026, en
 * `rows[20].entity`, con un bundle anterior abierto.
 *
 * Una entidad desconocida viaja con un nombre que el tipo no contempla; el
 * motor la busca en `ENTITY_TO_TABLE` y la salta si no esta. Por eso el
 * `as` de abajo: miente en el tipo exactamente lo que el motor ya comprueba.
 */
const filaDelCable = z.object({ entity: z.string().min(1), row: z.record(z.string(), z.unknown()) });
const pushDelCable = pushResultSchema.extend({ rows: z.array(filaDelCable) });
const pullDelCable = pullResultSchema.extend({ rows: z.array(filaDelCable) });

/**
 * Transporte real contra Supabase. Usa dos funciones RPC (`sync_push` y
 * `sync_pull`) invocadas con el JWT del usuario, de modo que RLS sigue
 * aplicando (docs/SYNC_ENGINE.md §3).
 */
export function createSupabaseTransport(client: SupabaseClient): SyncTransport {
  return {
    async push(ops: OutboxOp[]): Promise<PushResult> {
      const payload = ops.map((op) => ({
        op_id: op.opId,
        entity: op.entity,
        entity_id: op.entityId,
        operation: op.operation,
        payload: keysToSnake(op.payload),
      }));

      const { data, error } = await client.rpc('sync_push', { ops: payload });
      if (error) throw new Error(`sync_push: ${error.message}`);
      return pushDelCable.parse(data) as PushResult;
    },

    async pull(cursor: number, limit: number): Promise<PullResult> {
      const { data, error } = await client.rpc('sync_pull', {
        cursor_seq: cursor,
        page_size: limit,
      });
      if (error) throw new Error(`sync_pull: ${error.message}`);
      return pullDelCable.parse(data) as PullResult;
    },
  };
}
